import React from 'react';
import { CheckIcon, ClockIcon, SnowflakeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { rides } from '../data/rides';
import { stable } from '../data/stable';
import { fmt, formatPrice } from '../utils/format';
const routeImages = {
  forest: 'https://cdn.magicpatterns.com/patterns/generated-images/90161693-b991-4f03-a476-a81e68bf107e.jpg',
  sea: 'https://cdn.magicpatterns.com/patterns/generated-images/028d7a4d-a443-4685-a6bc-9a9133f7e4e2.jpg'
};
interface RidesSectionProps {
  onBook: (rideId: string) => void;
}
export function RidesSection({
  onBook
}: RidesSectionProps) {
  const {
    t,
    l
  } = useLanguage();
  const r = t.rides;
  const locale = t.meta.locale;
  const routes = [{
    id: 'forest' as const,
    title: r.forest,
    text: r.forestDesc
  }, {
    id: 'sea' as const,
    title: r.sea,
    text: r.seaDesc
  }];
  const extras = [{
    title: r.gift,
    text: r.giftDesc
  }, {
    title: r.pack,
    text: r.packDesc
  }, {
    title: r.private,
    text: r.privateDesc
  }];
  return <section id="rides" aria-labelledby="rides-title" className="scroll-mt-16 bg-linen py-20 lg:py-28" data-id="element-212" data-mp="27svua0tiqwhz">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-213" data-mp="2cqf6kv6noz7a">
        <div className="max-w-2xl" data-id="element-214" data-mp="2hoytoywzkh18">
          <h2 id="rides-title" className="font-serif text-4xl font-bold text-bark md:text-5xl" data-id="element-215" data-mp="2hnximufyzuji">
            {r.title}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-bark/75" data-id="element-216" data-mp="2qhkai9h24dnf">{r.intro}</p>
        </div>

        <div className="mt-12 grid gap-10 md:grid-cols-2" data-id="element-217" data-mp="2vgn5t278j5i7">
          {routes.map(route => <div key={route.id} data-id="element-218" data-mp="2vigbae643jom">
              <div className="aspect-[16/10] overflow-hidden rounded-lg" data-id="element-219" data-mp="30fgjovqfoyiw">
                <img src={routeImages[route.id]} alt="" className="h-full w-full object-cover" loading="lazy" data-id="element-220" data-mp="35et99o8owjp6" />
              </div>
              <h3 className="mt-5 font-serif text-2xl font-bold text-bark" data-id="element-221" data-mp="162qqyhbsxvd3">{route.title}</h3>
              <p className="mt-2 max-w-lg leading-relaxed text-bark/75" data-id="element-222" data-mp="1b00e1qbpxuxr">{route.text}</p>
            </div>)}
        </div>

        <div className="mt-16 overflow-hidden rounded-xl border border-line bg-cream" data-id="element-223" data-mp="130m6m62tpl54">
          <div className="hidden grid-cols-[2fr_1.3fr_1fr_1fr_auto] gap-6 border-b border-line px-6 py-3 text-xs font-bold uppercase tracking-wider text-bark/60 md:grid" data-id="element-224" data-mp="y2ax5wgfxcnf">
            <span data-id="element-225" data-mp="17ynowosfdgfj">{r.colRide}</span>
            <span data-id="element-226" data-mp="17xm5nx199nmq">{r.colLevel}</span>
            <span data-id="element-227" data-mp="1lyyi3yh7j73v">{r.colDuration}</span>
            <span data-id="element-228" data-mp="1lxwwj02kf27w">{r.colPrice}</span>
            <span className="w-24" data-id="element-229" data-mp="1lwvjwr5wi7o2" />
          </div>
          <ul className="divide-y divide-line" data-id="element-230" data-mp="1h0n91pyql0mp">
            {rides.map(ride => <li key={ride.id} className="grid grid-cols-2 items-center gap-x-6 gap-y-2 px-6 py-5 md:grid-cols-[2fr_1.3fr_1fr_1fr_auto]" data-id="element-231" data-mp="1vpb1voh76w8y">
                <div className="col-span-2 md:col-span-1" data-id="element-232" data-mp="24p6ii8tivjgd">
                  <p className="font-serif text-xl font-bold text-bark" data-id="element-233" data-mp="24pppiuhmwnfm">{l(ride.title)}</p>
                  <p className="text-sm text-bark/65" data-id="element-234" data-mp="20pflhndek73e">
                    {t.routes[ride.route]} · {l(ride.description)}
                  </p>
                </div>
                <p className="text-[15px] font-medium text-saddle" data-id="element-235" data-mp="2obhswdg4r0se">{l(ride.level)}</p>
                <p className="flex items-center gap-1.5 text-[15px] text-bark/80" data-id="element-236" data-mp="2abqh8jd35whk">
                  <ClockIcon className="h-4 w-4 text-bark/50" aria-hidden="true" data-id="element-237" data-mp="2fajnbqq1ml7p" />
                  {ride.durationMin} {r.min}
                </p>
                <p data-id="element-238" data-mp="20kvkgl27rehv">
                  <span className="font-serif text-2xl font-bold text-bark" data-id="element-239" data-mp="20kcd1ya6w8i3">{formatPrice(ride.price, locale)}</span>
                  <span className="ml-1 text-xs text-bark/60" data-id="element-240" data-mp="25egigd3t48h2">{ride.groupSize ? r.perGroup : r.perRider}</span>
                </p>
                <button type="button" onClick={() => onBook(ride.id)} className="w-24 justify-self-end whitespace-nowrap rounded-full border border-saddle px-4 py-2 text-sm font-bold text-saddle transition-colors duration-150 hover:bg-saddle hover:text-cream" data-id="element-241" data-mp="1vm2hvnogt7wh">
                  {r.book}
                </button>
              </li>)}
          </ul>
          <p className="border-t border-line bg-straw/40 px-6 py-4 text-sm leading-relaxed text-bark/80" data-id="element-242" data-mp="2kz038q6oojkc">{r.prepNote}</p>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12" data-id="element-243" data-mp="2g4vuc1duy1gm">
          <div className="lg:col-span-7" data-id="element-244" data-mp="2b6uahe8lxy43">
            <h3 className="font-serif text-2xl font-bold text-bark" data-id="element-245" data-mp="2b62rr403zuv8">{r.includedTitle}</h3>
            <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-246" data-mp="266pwbuv69fh1">
              {r.included.map(item => <li key={item} className="flex items-start gap-2.5 text-[15px] leading-snug text-bark/85" data-id="element-247" data-mp="1x84g46d6acjf">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-moss" aria-hidden="true" data-id="element-248" data-mp="1s91d0rgr6ge3" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="space-y-4 lg:col-span-5" data-id="element-249" data-mp="3rjsvg9nbgh4c">
            <div className="rounded-lg bg-bark p-5 text-cream" data-id="element-250" data-mp="3modntzfwjhgd">
              <p className="flex items-center gap-2 font-serif text-xl font-bold" data-id="element-251" data-mp="3u6v36maglqh9">
                <SnowflakeIcon className="h-5 w-5 text-hay" aria-hidden="true" data-id="element-252" data-mp="30aeh97vmune" />
                {r.winterTitle}
              </p>
              <p className="mt-1.5 text-[15px] leading-relaxed text-cream/85" data-id="element-253" data-mp="7qqsovjiukat">
                {fmt(r.winterText, {
                amount: formatPrice(stable.winterDiscount, locale)
              })}
              </p>
            </div>
            <div className="rounded-lg border border-line bg-cream p-5" data-id="element-254" data-mp="3b8r3sjkdj5sn">
              <p className="flex items-center gap-2 font-bold text-bark" data-id="element-255" data-mp="3g6k5p0en9dn4">
                <span className="rounded bg-moss px-1.5 py-0.5 text-xs font-bold text-cream" data-id="element-256" data-mp="3g8n7ehp5sv7o">Stebby</span>
                {r.stebbyTitle}
              </p>
              <p className="mt-1.5 text-[15px] leading-relaxed text-bark/75" data-id="element-257" data-mp="3p8jtcbk5zfz1">{r.stebbyText}</p>
            </div>
          </div>
        </div>

        <div className="mt-12" data-id="element-258" data-mp="10i0omzmgk5at">
          <h3 className="font-serif text-xl font-bold text-bark" data-id="element-259" data-mp="14gq9fd9tsp4h">{r.extrasTitle}</h3>
          <dl className="mt-4 grid gap-6 sm:grid-cols-3" data-id="element-260" data-mp="31hng51tydvqu">
            {extras.map(extra => <div key={extra.title} className="border-l-2 border-hay pl-4" data-id="element-261" data-mp="2wiswxys2q567">
                <dt className="font-bold text-bark" data-id="element-262" data-mp="2wkx4cqp6lmb8">{extra.title}</dt>
                <dd className="mt-1 text-[15px] text-bark/70" data-id="element-263" data-mp="3ahagdkay4vib">{extra.text}</dd>
              </div>)}
          </dl>
        </div>
      </div>
    </section>;
}